import { DialogM } from "@common/components/alert/dialog/Dialog"
import FilledButtonM from "@common/components/button/FilledButtonM"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { useApiStatus } from "@common/hooks/useApiStatus"
import useLaunchActivity from "@common/hooks/useLaunchActivity"
import { theme } from "@common/theme/theme"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { useIsFocused } from "@react-navigation/native"
import { activitiesService } from "@data/services/activitiesService"
import { hasRole } from "@data/utility/ability"
import {
    countSameActivitiesToday,
    countSameAgainActivities,
    findItemInListWithId,
} from "@data/utility/dataUtils"
import { StatusBar } from "@common/systemBars/StatusBar"
import React, { useContext, useEffect, useState, useCallback } from "react"
import { View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"
import { BadgeM } from "../../../common/components/badge/BadgeM"
import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { cleanMediaUrl } from "../../../data/constants/apiConstants"
import MaterialModal from "../execute/MaterialDisplayModal"
import StaticOneStepActivityHeader from "../one-step/StaticOneStepActivityHeader"
import { useCorrectionPolling } from "../../../engine/activity/correction/useCorrectionPolling"
import { submittedWorkImages } from "../../../engine/activity/correction/correctionFlow"
import ActivityDetailsModal from "./ActivityDetailsModal"
import ReadReviewRequestHandler from "./ReadReviewRequestHandler"

const ActivityFinishedScreen = (props) => {
    // ** Component props
    const { navigation, route } = props

    const activityId = props.currentActivity?.activityData?.id

    const userChallenges = props.user?.challenges?.concat(
        props.user?.history?.challenges
    )

    // ** Component variables
    const activity = props.currentActivity?.activityData

    const activityTemplateId =
        activity?.activity_template_id ?? activity?.activityTemplateId

    const activityTemplate = activity
        ? findItemInListWithId(activityTemplateId, props.activityTemplates)
        : null
    const challenge = activity?.challenge_id
        ? findItemInListWithId(activity?.challenge_id, userChallenges)
        : null
    const schoolSubject = activity?.school_subject_id
        ? findItemInListWithId(
              activity?.school_subject_id,
              props.schoolSubjects
          )
        : challenge?.school_subject

    const activitiesList = activity ? [activity] : []
    const sameActivitiesCount = challenge?.planning_mode
        ? countSameAgainActivities(
              activityTemplate,
              activitiesList,
              activity?.id
          )
        : countSameActivitiesToday(
              activityTemplate,
              activitiesList,
              challenge ? challenge?.id : null,
              schoolSubject ? schoolSubject?.id : null
          )
    const maxReached = sameActivitiesCount >= activityTemplate?.max_per_day

    const maxReachedTitle = "Nombre max atteint"
    const maxReachedText =
        "Tu as atteint le maximum d'activités '" +
        activityTemplate?.title +
        "' sur " +
        (challenge?.id ? "ce défi" : "cette matière") +
        " aujourd'hui. Tu peux faire d'autres activités ou revenir demain."
    const requestApiService = activitiesService.activityTodoAsStudent
    const launchActivity = useLaunchActivity()

    const { sendLiveKitData } = useLiveKitEventSender()

    const formData = activityTemplate?.steps
    const activityType = formData?.find((step) => step.type === "photo")
        ? "photo"
        : "audio"

    const [isModalVisible, setIsModalVisible] = useState(false)

    useApiStatus(
        requestApiService, // api service
        null, // success message on toast (string or null)
        true, // error message on toast (true) or keep it in redux state (false)
        (data) => {
            setIsModalVisible(false)
            sendLiveKitData(
                "newTodo",
                {
                    id: props.user?.id,
                    activity: data,
                },
                props.user?.id
            )
            if (props.isLiveModeEnabled) {
                sendLiveKitData(
                    "refreshTodoListForTutor",
                    {
                        id: props.user?.id,
                        activity: data,
                    },
                    props.user?.id
                )
            }
            if (hasRole(props.user, "student")) {
                launchActivity(data)
            }
        } // success callback
    )

    const onRetry = () => {
        if (maxReached) {
            DialogM.show({
                text1: maxReachedTitle,
                text2: maxReachedText,
            })
            return
        }
        props.apiRequest(
            requestApiService,
            hasRole(props.user, "student") ? {} : { id: props.user?.id },
            {
                activity_template_id: activityTemplate?.id,
                school_subject_id: schoolSubject?.id,
                material_id: activity?.material?.id,
                material_chapter: activity?.chapter,
                estimated_duration: activity?.estimated_duration,
                chapter_id: activity?.student_chapter?.chapter?.id,
                chapter_name: activity?.student_chapter?.chapter?.name,
                chapter_class: activity?.student_chapter?.chapter?.class,
            }
        )
    }

    const refresh = useCallback(() => {
        props.apiRequest(
            activitiesService.getActivities,
            {},
            {},
            {
                with_logs: 1,
                activity_ids: activityId,
            }
        )
    }, [activityId])

    useEffect(() => {
        refresh()
    }, [activityId])


    // Celui-ci se tait sous l'attente de la correction, qui fait son propre
    // relevé, et sous l'écran de fin, où personne ne le regarde : un relevé
    // par le store redessine tout l'écran à chaque réponse.
    const isFocused = useIsFocused()
    useCorrectionPolling(
        isFocused &&
            !props.correctionInFlow &&
            (activity?.status === "pending_review" ||
                !!activity?.correction_generating),
        refresh
    )

    const dimensions = useContext(DimensionsContext)

    // La copie seule : l'image de correction a sa propre place en tête.
    const images = submittedWorkImages(activity)

    const recordingURI =
        activityType === "audio"
            ? cleanMediaUrl(
                  activity?.activity_logs?.find(
                      (log) => log?.action === "step_media_sent"
                  )?.work_attachment
              )
            : null

    // Correction status helpers
    const correctionStatus = activity?.correction_status
    const correctionGenerating = activity?.correction_generating
    const correctionImage = activity?.correction_image

    // Determine button label based on correction status
    const getButtonLabel = () => {
        if (correctionGenerating) {
            return "Correction en cours..."
        }
        if (correctionStatus === "completed" && correctionImage) {
            return "Voir ma correction"
        }
        return "Voir mon rendu"
    }

    const renderButtonIcon = () => {
        return (
            <View
                style={{
                    backgroundColor: theme.colors.black,
                    height: 50,
                    width: 50,
                    alignItems: "center",
                    justifyContent: "center",
                    alignSelf: "flex-start",
                    right: 12,
                    borderTopLeftRadius: 15,
                    borderBottomLeftRadius: 15,
                }}
            >
                <>
                    <FontAwesome5
                        name={
                            activityType === "photo" ? "camera" : "microphone"
                        }
                        size={18}
                        color={theme.colors.white}
                        style={{
                            alignSelf: "center",
                        }}
                    />
                    {!images.length && props.getActivitiesRequest ? (
                        <ActivityIndicator
                            animating={true}
                            style={{
                                position: "absolute",
                                bottom: 5,
                                right: 10,
                            }}
                            size={16}
                            color={theme.colors.success}
                        />
                    ) : (
                        <>
                            {activityType === "photo" && (
                                <BadgeM
                                    style={{
                                        backgroundColor: theme.colors.success,
                                        position: "absolute",
                                        bottom: 5,
                                        right: 10,
                                        color: theme.colors.white,
                                    }}
                                    count={images.length}
                                    size={16}
                                />
                            )}
                        </>
                    )}
                </>
            </View>
        )
    }

    useEffect(() => {
        if (
            activity &&
            !activity?.is_review_read &&
            hasRole(props.user, "student")
        ) {
            props.apiRequest(activitiesService.readReview, { id: activity?.id })
        }
    }, [activity])

    const activityMaterial = activity?.material
    const [isOpen, setIsOpen] = useState(false)

    const handleShowModal = () => setIsOpen(!isOpen)
    const status = activity?.finished_at
        ? null
        : activity?.started_at !== null && activity?.locked_at !== null
        ? "ongoing"
        : activity?.started_at !== null && activity?.locked_at === null
        ? "started"
        : null

    return (
        <View style={{ flex: 1 }}>
            <ReadReviewRequestHandler />
            <View
                style={{
                    flex: 1,
                    alignItems: "center",
                    alignContent: "flex-start",
                    justifyContent: "flex-start",
                    alignSelf: "center",
                    paddingHorizontal: 15,
                    width: "100%",
                }}
            >
                {activity?.id !== activityId ? (
                    <ActivityIndicator
                        animating={true}
                        style={{ marginTop: 10 }}
                        color={theme.colors.primary}
                    />
                ) : (
                    <View style={{ flex: 1, width: dimensions?.width < 800 ? dimensions?.width : null  }}>
                        <StaticOneStepActivityHeader
                            activity={activity}
                            route={route}
                            onExit={() => navigation.goBack()}
                        />

                        <View
                            style={{
                                flex: 1,
                                paddingHorizontal: 15,
                            }}
                        >
                            <MaterialModal
                                handleShowModal={handleShowModal}
                                isOpen={isOpen}
                                material={activityMaterial}
                                noModal
                                status={status}
                            />
                        </View>
                        <View
                            style={{
                                marginBottom: 20,
                            }}
                        >
                            <FilledButtonM
                                color={theme.colors.success}
                                onPress={() => setIsModalVisible(true)}
                                style={{ alignSelf: "center" }}
                                icon={renderButtonIcon}
                                contentStyle={{
                                    justifyContent:
                                        images.length > 0 &&
                                        activityType === "photo"
                                            ? "space-between"
                                            : "center",
                                    alignItems: "center",
                                }}
                                condition={
                                    images.length > 0 &&
                                    activityType === "photo"
                                }
                                labelStyle={{
                                    fontSize: 12,
                                    flex: 1,
                                    right: 12,
                                }}
                                label={getButtonLabel()}
                            />
                        </View>
                    </View>
                )}

                <ActivityDetailsModal
                    activity={activity}
                    visible={isModalVisible}
                    onClose={() => setIsModalVisible(false)}
                    activityType={activityType}
                    images={images}
                    recordingURI={recordingURI}
                    maxReached={maxReached}
                    onRetry={onRetry}
                    nav={navigation}
                    correctionStatus={correctionStatus}
                    correctionGenerating={correctionGenerating}
                    correctionImage={correctionImage}
                    onRefresh={refresh}
                />
            </View>
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />
        </View>
    )
}
const mapStateToProps = (state) => {
    return {
        getActivitiesRequest: state.api.getActivities?.request,
        getActivitiesSuccess: state.api.getActivities?.success,
        getActivitiesError: state.api.getActivities?.error,
        activityTemplates: state.data.staticData?.activity_templates,
        schoolSubjects: state.data.staticData?.school_subjects,
        user: state.data.currentUser?.object,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        liveSessionId: state.liveMode?.liveSessionId,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        activityTodoAsStudentRequest: state.api.activityTodoAsStudent?.request,
        activityTodoAsTutorRequest: state.api.activityTodoAsTutor?.request,
        currentActivity: state.data?.currentActivity?.activity,
        isLiveModeEnabled: state.liveMode?.isLiveModeEnabled,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ActivityFinishedScreen)
